import React, {useCallback, useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {browserTimeZone, dateToLocalizedFormat, stringToDate} from "common/src/utils/DateUtils";
import {useModal} from "../../../context/ModalProvider";
import Button from "common/src/components/widget/Button";
import FloatingDateTimePicker from "common/src/components/widget/FloatingDateTimePicker";
import FloatingDateRangePicker from "common/src/components/widget/FloatingDateRangePicker";
import {UserContext} from "../../../context/UserContext";
import {fetchDataPreview} from "../../../state/api/datasetSliceApi";
import {useDispatch, useSelector} from "react-redux";
import {ArrayBuffer} from "core-js/internals/array-buffer";
import {addErrorNotification} from "../../../state/api/notifications";

// Every keyword the backend's dynamic filters support. Add an entry here (and a matching
// TimestampKeyword value on the backend) to support another one - nothing else in this file
// needs to know what a keyword actually means, only its code, wire value, and label.
const KEYWORDS = [
    {code: "YESTERDAY", overrideValue: "YESTERDAY", defaultLabel: "yesterday"},
    {code: "LAST_WEEK", overrideValue: "LAST_WEEK", defaultLabel: "last week"},
    {code: "LAST_MONTH", overrideValue: "LAST_MONTH", defaultLabel: "last month"},
];
const keywordByCode = (code) => KEYWORDS.find(k => k.code === code);
const keywordByOverrideValue = (value) => typeof value === "string"
    ? KEYWORDS.find(k => k.overrideValue.toLowerCase() === value.toLowerCase())
    : undefined;

const DataSetCard = ({data}) => {
    const {t} = useTranslation();
    const {openModal, closeModal} = useModal();
    const dispatch = useDispatch();
    const {user} = useContext(UserContext);
    const [showPreview, setShowPreview] = useState(false);
    // Overrides for single-point dynamic filters (isRange: false), keyed by columnName -> date.
    const [dateOverrides, setDateOverrides] = useState({});
    // Overrides for range dynamic filters (isRange: true), keyed by columnName -> {from, to}.
    const [rangeOverrides, setRangeOverrides] = useState({});
    const previewedData = useSelector(state => state.datasetSliceApi.preview[data.id]?.data);

    const isLoadingPreviewById = useSelector(state => state.datasetSliceApi.preview[data.id]?.loadingPreview);
    const isLoadingPreview = useSelector(state => state.datasetSliceApi.loadingPreview);
    const isLoadingCsvById = useSelector(state => state.datasetSliceApi.preview[data.id]?.csvLoading);
    const isLoadingCsv = useSelector(state => state.datasetSliceApi.csvLoading);

    const [downloadCsvBusy, setDownloadCsvBusy] = useState(false);

    const displayPreviewData = useCallback(() => {
        openModal({
            title: t("preview"),
            body: (
                <div>
                    <pre>
                        {JSON.stringify(previewedData, null, 2)}
                    </pre>
                </div>
            ),
            onClose: closeModal,
            backdropClose: true
        });
    }, [previewedData]);
    const displayDataStructure = () => {
        const dataSetColumnKeys = Object.keys(data.columnDescription);
        openModal({
            title: t("dataStructure"),
            body: (
                <>
                    <div className="level ">
                        <div className="level-left level-item">
                            <div>
                                <p className="heading">{t("columnDescriptions")}</p>
                                <ul>
                                    {dataSetColumnKeys.length > 0 && dataSetColumnKeys.map(columnKey => (
                                        <li key={columnKey}>
                                            <b>{columnKey}:</b> {data.columnDescription[columnKey]}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="level">
                        <div className="level-item has-text-centered">
                            <div>
                                <p className="heading">{t("dataset")}</p>
                                <p>
                                    <b>{data.name}</b>
                                </p>
                            </div>
                        </div>
                        <div className="level-item has-text-centered">
                            <div>
                                <p className="heading">{t("id")}</p>
                                <p>{data.id}</p>
                            </div>
                        </div>
                        <div className="level-item has-text-centered">
                            <div>
                                <p className="heading">{t("version")}</p>
                                <p>{data.version}</p>
                            </div>
                        </div>
                    </div>
                </>
            ),
            onClose: closeModal,
            backdropClose: true
        });
    };

    useEffect(() => {
        if (previewedData && showPreview) {
            setShowPreview(false);
            displayPreviewData();
        } else if (previewedData && !showPreview) {
            setShowPreview(false);
        }
    }, [previewedData, showPreview]);
    const userAuthorized = useCallback(() => {
        return !!(user && user.accessToken && user.hasLoaded);
    }, [user]);


    const fetchPreview = () => {
        if (!data || !data.id) {
            console.error(t("dataOrDataIdMissing"));
            return;
        }
        const hasDynamicFilters = (data.dynamicFilters || []).length > 0;
        if (previewedData && !hasDynamicFilters) {
            setShowPreview(true);
        } else {

            dispatch(fetchDataPreview({...data, overrideParams: buildOverrideParams()})).unwrap()
                .then(() => {
                    setShowPreview(true);
                })
                .catch((error) => {
                    dispatch(addErrorNotification(t("Error"), t("Failed to fetch preview.")));
                    console.error("Error fetching preview", error);
                });
        }
    };

      async function downloadCSV() {
        const url = appendParams(data.isPublic
            ? `/api/v1/data/public/${data.id}/csv`
            : `/api/v1/data/private/${data.id}/csv`, buildOverrideParams());
        let filename = ("" + data.name).replace(/[^a-z0-9]/gi, '_') + ".csv";
        setDownloadCsvBusy(true);
        await fetch(url, {
            headers: {
                'Accept': 'text/csv',
                'Content-Type': 'text/csv',
                'Authorization': "Bearer " + user.accessToken,
            },
            responseType: ArrayBuffer

        }).then(response => {
            response.arrayBuffer().then(data => {
                const blob = new Blob([...[data.slice(0)]], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const element = document.createElement('a');
                element.href = url;
                element.setAttribute('download', filename);
                document.body.appendChild(element);
                element.click();
                document.body.removeChild(element);
                window.URL.revokeObjectURL(url);
            })
            }).catch(
            error => {
                console.error('Error during file download:', error);
                alert(`Error: ${error.message}`);
            }
        ).finally(() => {
            setDownloadCsvBusy(false);
        });
    }

    // A single open bound, or a keyword override, is always valid - this only matters once both
    // ends of a range are set.
    const isRangeOrderInvalid = (columnName) => {
        const range = rangeOverrides[columnName] || {};
        return !range.keyword && !!range.start && !!range.end
            && stringToDate(range.start, false, true) > stringToDate(range.end, false, true);
    };

    const buildOverrideParams = () => {
        const dynamicFilters = data?.dynamicFilters || [];
        const singleDynamicColumn = dynamicFilters.length === 1 ? dynamicFilters[0].columnName : null;
        const keyFor = (verb, columnName) => singleDynamicColumn ? verb : `${verb}.${columnName}`;
        const params = [`timeZone=${encodeURIComponent(browserTimeZone())}`];
        dynamicFilters.forEach(filter => {
            if (filter.isRange) {
                const range = rangeOverrides[filter.columnName] || {};
                if (range.keyword) {
                    // A keyword covers the whole range - sent as "date", not "from"/"to".
                    const overrideValue = keywordByCode(range.keyword)?.overrideValue;
                    if (overrideValue) {
                        params.push(`${encodeURIComponent(keyFor("date", filter.columnName))}=${encodeURIComponent(overrideValue)}`);
                    }
                    return;
                }
                if (isRangeOrderInvalid(filter.columnName)) {
                    // Skip a guaranteed-to-fail request - fall back to the column's default instead.
                    return;
                }
                // "start"/"end" is this app's own naming; the wire params are "from"/"to".
                if (range.start) {
                    params.push(`${encodeURIComponent(keyFor("from", filter.columnName))}=${encodeURIComponent(range.start)}`);
                }
                if (range.end) {
                    params.push(`${encodeURIComponent(keyFor("to", filter.columnName))}=${encodeURIComponent(range.end)}`);
                }
            } else if (dateOverrides[filter.columnName]) {
                params.push(`${encodeURIComponent(keyFor("date", filter.columnName))}=${encodeURIComponent(dateOverrides[filter.columnName])}`);
            }
        });
        return params;
    };

    const keywordLabel = (code) => code ? t(`dynamicFilterKeyword${code}`, {defaultValue: keywordByCode(code)?.defaultLabel || code}) : null;
    const keywordOptions = KEYWORDS.map(k => ({code: k.code, label: keywordLabel(k.code)}));

    // The configured default is either a keyword (e.g. "yesterday") or a literal timestamp, shown
    // as the field's own placeholder value.
    const pointDefaultLabel = (filter) => {
        if (filter.keyword) {
            return keywordLabel(filter.keyword);
        }
        return filter.defaultStart ? dateToLocalizedFormat(filter.defaultStart) : null;
    };

    const rangeDefaultLabel = (filter, bound) => {
        if (filter.keyword) {
            return keywordLabel(filter.keyword);
        }
        const boundValue = bound === "start" ? filter.defaultStart : filter.defaultEnd;
        return boundValue ? dateToLocalizedFormat(boundValue) : null;
    };

    const appendParams = (url, params) => {
        if (!params || params.length === 0) {
            return url;
        }
        return url + (url.includes("?") ? "&" : "?") + params.join("&");
    };

    const getDataUrl = () => {
        if (!data || !data.id) {
            return "";
        }
        const base = data.isPublic
            ? `${window.location.origin}/api/v1/data/public/${data.id}`
            : `${window.location.origin}/api/v1/data/private/${data.id}`;
        return appendParams(base, buildOverrideParams());
    };

    const copyAsCurl = useCallback(() => {
        if (!data || !data.id) {
            console.error(t("dataOrDataIdMissing"));
            return;
        }

        const previewUrl = appendParams(data.isPublic
            ? `${window.location.origin}/api/v1/data/public/${data.id}/preview`
            : `${window.location.origin}/api/v1/data/private/${data.id}/preview`, buildOverrideParams());

        const token = user?.accessToken; // Replace with your actual token

        let curlCommand = `curl -X GET '${previewUrl}'`
        curlCommand += (token != null ? ` -H 'Authorization: Bearer ${token}'` : '');
        copyToClipboard(curlCommand);
    }, [user, data, dateOverrides, rangeOverrides]);

    const copyAsFetch = useCallback(() => {
        if (!data || !data.id) {
            console.error(t("dataOrDataIdMissing"));
            return;
        }

        const previewUrl = appendParams(data.isPublic
            ? `${window.location.origin}/api/v1/data/public/${data.id}/preview`
            : `${window.location.origin}/api/v1/data/private/${data.id}/preview`, buildOverrideParams());

        const token = user?.accessToken; // Replace with your actual token
        const authorizationHeader = `,
        headers: {
            'Authorization': 'Bearer ${token}'
        }
`;
        const fetchCommand = `
fetch('${previewUrl}', {
    method: 'GET' ${authorizationHeader}
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));
`;

        copyToClipboard(fetchCommand);
    }, [user, data, dateOverrides, rangeOverrides]);

    return (
        <div className="data-set-card card ">
            <header className="card-header level-left ">
                <div className="card-header-title ">

                            <span className="icon fz solid fz-binoculars medium mr-2"/>

                            {data?.isPublic
                                ? <span className="icon "><i className="fz fz-lock-open mr-2"/>&nbsp;</span>
                                : <span className="icon"><i className="fz fz-lock mr-2"/>&nbsp;</span>}

                            <span className={""} title={data.name}><b>{data.name}</b></span>

                </div>
            </header>

            <div className="card-content">
                <div className="level">
                    <div>
                        <p className="heading">{t("description")}</p>
                        <div className="multiline-text" title={data?.description}>
                            {data?.description}
                        </div>
                    </div>
                </div>

                <div className="level is-mobile">
                    <div>
                        <div className="level-left level-item">
                            <div>
                                <p className="heading">{t("posted")}</p>
                                <p>{dateToLocalizedFormat(data?.createdAt)}</p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="level-left level-item">
                            <div>
                                <p className="heading">{t("modified")}</p>
                                <p>{dateToLocalizedFormat(data?.lastModifiedAt)}</p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="level-left level-item">
                            <div>
                                <p className="heading">{t("version")}</p>
                                <p>V{data?.version}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {data?.dynamicFilters?.length > 0 &&
                    // .card-content sets white-space: nowrap (for single-line fields like the Data
                    // URL below) - reset it here so the range-order warning text can actually wrap
                    // instead of overflowing into a scrollbar.
                    <div className="level is-mobile" style={{flexWrap: "wrap", whiteSpace: "normal"}}>
                        {data.dynamicFilters.map(filter => (
                            <div key={filter.columnName} className="mr-3 mb-3" style={{maxWidth: "280px", minWidth: 0}}>
                                <p className="heading">{filter.columnName.toUpperCase()} - {filter.operation}</p>
                                {filter.isRange
                                    ? <FloatingDateRangePicker
                                        startLabel={t("dynamicFilterStart")}
                                        endLabel={t("dynamicFilterEnd")}
                                        keywordsLabel={t("dynamicFilterKeywords")}
                                        type={"datetime"}
                                        keywordOptions={keywordOptions}
                                        value={rangeOverrides[filter.columnName] || null}
                                        startPlaceholder={rangeDefaultLabel(filter, "start") || undefined}
                                        endPlaceholder={rangeDefaultLabel(filter, "end") || undefined}
                                        onChange={(value) => setRangeOverrides({
                                            ...rangeOverrides, [filter.columnName]: value || {}
                                        })}
                                    />
                                    : <FloatingDateTimePicker
                                        label={t("dynamicFilterDate")}
                                        keywordsLabel={t("dynamicFilterKeywords")}
                                        type={"datetime"}
                                        keywordOptions={keywordOptions}
                                        value={dateOverrides[filter.columnName]
                                            ? (keywordByOverrideValue(dateOverrides[filter.columnName])
                                                ? {keyword: keywordByOverrideValue(dateOverrides[filter.columnName]).code}
                                                : {date: dateOverrides[filter.columnName]})
                                            : null}
                                        placeholder={pointDefaultLabel(filter) || undefined}
                                        onChange={(value) => setDateOverrides({
                                            ...dateOverrides,
                                            [filter.columnName]: value?.keyword ? keywordByCode(value.keyword)?.overrideValue : value?.date
                                        })}
                                    />
                                }
                                {filter.isRange && isRangeOrderInvalid(filter.columnName) &&
                                    <p className="help is-danger" style={{wordBreak: "break-word"}}>{t("dynamicFilterRangeOrderInvalid")}</p>
                                }
                            </div>
                        ))}
                    </div>
                }

                <div className="level marginless-bottom is-full-mobile is-mobile">
                    <div className="is-fullwidth">
                        <div className="is-fullwidth">
                            <p className="heading">{t("dataUrl")}</p>
                            <div className="field has-addons is-fullwidth">
                                <>
                                    <p className="control is-expanded">
                                        <input
                                            className="input"
                                            type="text"
                                            readOnly={true}
                                            value={getDataUrl()}
                                        />
                                    </p>
                                    <p className="control is-clickable">
                                        <button
                                            className="button"
                                            onClick={() => {
                                                copyToClipboard(getDataUrl());
                                            }}
                                        >
                                            <span className="icon fz fz-clipboard" title={t("copyToClipboard")}/>
                                        </button>
                                    </p>
                                    {userAuthorized() && <p className="control is-clickable">
                                        <button
                                            className="button"
                                            onClick={() => {
                                                copyAsCurl();
                                            }}
                                        >
                                            <span className="icon fz fz-list-edit" title={t("copyAsCurl")}/>
                                        </button>
                                    </p>}
                                    {userAuthorized() && <p className="control is-clickable">
                                        <button
                                            className="button"
                                            onClick={() => {
                                                copyAsFetch();
                                            }}
                                        >
                                            <span className="icon fz fz-search-list" title={t("copyAsFetch")}/>
                                        </button>
                                    </p>}
                                </>

                            </div>
                        </div>
                    </div>
                </div>

                <div className="level  is-mobile">
                    {userAuthorized() && <div className=" ">

                        <Button label={t("csv")}
                                className="is-danger mb-2"
                                color="primary"
                                isOutlined={true}
                                isLoading={isLoadingCsvById || downloadCsvBusy}
                                disabled={isLoadingCsv || downloadCsvBusy}
                                onClick={downloadCSV}
                        />
                    </div>}
                    <div className=" ">
                        <Button
                            label={t("dataStructure")}
                            className="is-danger mb-2"
                            color="primary"
                            isOutlined={true}
                            onClick={() => {
                                displayDataStructure();
                            }}
                        />
                    </div>
                    {userAuthorized() &&
                        <div className=" ">
                            <Button
                                label={t("preview")}
                                className="is-danger mb-2"
                                color="primary"
                                isOutlined={true}
                                onClick={() => {
                                    fetchPreview();
                                }}
                                isLoading={isLoadingPreviewById}
                                disabled={isLoadingPreview}
                            />
                        </div>}
                </div>
            </div>
        </div>
    );
};

export default DataSetCard;

const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        // navigator.clipboard API is available
        return navigator.clipboard.writeText(text).then(() => {
            console.debug('Copied to clipboard successfully!');
        }).catch((err) => {
            console.error('Failed to copy to clipboard', err);
        });
    } else {
        // navigator.clipboard API not available, use legacy method
        let textArea = document.createElement("textarea");
        textArea.value = text;

        // Make the textarea element non-editable and move it out of view
        textArea.style.position = "fixed";
        textArea.style.top = "-9999px";
        textArea.style.left = "-9999px";

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        return new Promise((resolve, reject) => {
            try {
                document.execCommand('copy');
                document.body.removeChild(textArea);
                resolve();
                console.debug('Copied to clipboard successfully!');
            } catch (err) {
                document.body.removeChild(textArea);
                reject(err);
                console.error('Failed to copy to clipboard', err);
            }
        });
    }
};
